import axios, {
  type AxiosError,
  type AxiosInstance,
  type InternalAxiosRequestConfig,
} from 'axios';
import { toast } from 'sonner';
import {
  getActionableMessage,
  formatPermissionError,
} from '../utils/errorUtils';
import { logError } from '../utils/errorLogger';
import { clearAuthState, hasSessionHint, removeLegacyTokenKeys } from '../utils/authHelpers';
import { getAccessToken, setAccessToken, clearAccessToken } from '../auth/accessToken';
import { onAuthMessage, postAuthMessage, type AuthMessage } from '../auth/authChannel';
import type { Tenant, User } from './auth';
import { HARNESS_BASE_URL } from './harness-base-url';
import { loginPathFor } from '../utils/loginPath';

export { HARNESS_BASE_URL } from './harness-base-url';

declare module 'axios' {
  interface AxiosRequestConfig {
    /**
     * Epic 8 story 8.3 — suppress the shared interceptor's global 500+
     * "server error" toast AND its network-error toast for this request. For callers that render the
     * failure themselves: a dashboard item's data route answers three of its
     * four I4 codes as 5xx, and its error card already names the code, so a
     * toast per failing widget would duplicate it. Nothing else changes — the
     * error is still logged and still rejects to the caller.
     */
    skipErrorToast?: boolean;
  }
}

/**
 * PostgREST client — same-origin `/api/db` proxy (Vite forwards to :3003 in
 * dev, nginx in prod; same-origin is required by the page CSP
 * `connect-src 'self'`). Responses are NOT unwrapped to `.data`: callers
 * need the full Axios response for headers (e.g. `Content-Range` for
 * pagination totals).
 */
export const dbClient = axios.create({
  baseURL: import.meta.env.VITE_POSTGREST_URL || '/api/db',
  headers: {
    'Content-Type': 'application/json',
  },
});

/**
 * Agent-harness client (spec A3) — targets the harness service directly
 * (`HARNESS_BASE_URL`, `http://localhost:8080` in dev). Shares the
 * interceptor stack with dbClient so harness calls get the
 * in-memory bearer attach AND the queued 401→refresh-token replay —
 * hand-rolled axios calls would silently lose both. Like `dbClient`,
 * responses are NOT unwrapped: callers read `response.data` themselves,
 * which keeps their return shapes identical to plain axios.
 */
export const harnessClient = axios.create({
  baseURL: HARNESS_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

/**
 * Token-refresh endpoint on the agent harness (audit A1 chunk 3) — the sole
 * source of truth for the refresh URL. Its only caller is `postRefresh` below:
 * every refresh in the SPA goes through `refreshOnce`. The refresh token rides
 * the HttpOnly `amatis_refresh` cookie (Story 17.2), so the POST sends
 * `withCredentials: true` (dev is cross-origin :5173 → :8080). Points at the harness base
 * (`HARNESS_BASE_URL`, `:8080` in dev), NOT the legacy N8N base.
 */
export const REFRESH_URL = `${HARNESS_BASE_URL}/api/auth/refresh`;

// Request interceptor - add JWT auth token (the in-memory bearer, Story 17.2)
const attachAuth = (config: InternalAxiosRequestConfig) => {
  const token = getAccessToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  // Log requests in development
  if (import.meta.env.DEV) {
    console.log('[API Request]', config.method?.toUpperCase(), config.url);
  }

  return config;
};

// Track if we're currently refreshing to prevent multiple refresh calls.
// Module-level and SHARED across instances so concurrent 401s from harnessClient
// and dbClient coalesce into a single refresh.
let isRefreshing = false;
let failedQueue: Array<{
  resolve: (value: string) => void;
  reject: (reason: unknown) => void;
}> = [];

const processQueue = (error: Error | null, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else if (token) {
      prom.resolve(token);
    } else {
      // T-20260723-18 — every waiter MUST settle. `processQueue(null, null)`
      // previously fell through both arms and left the queue hanging forever
      // (a refresh that 200s with an empty access_token reaches exactly that).
      // Unreachable from today's callers; one line stops it being a trap for
      // the next one, now that `refreshOnce` widens who drains this queue.
      prom.reject(new Error('Token refresh failed'));
    }
  });

  failedQueue = [];
};

/**
 * Cross-tab refresh coordination (Story 17.2; replaces the localStorage
 * machinery of T-20260723-17 / T-20260723-11a).
 *
 * All tabs share ONE HttpOnly refresh cookie, and the refresh token is
 * single-use: when two tabs POST at once the loser's rotation is refused. The
 * tabs therefore talk over `BroadcastChannel('amatis-auth')` (`authChannel.ts`):
 *
 *   - before POSTing, a tab announces `refreshing`;
 *   - a tab that needs a refresh while a peer's `refreshing` is in flight waits
 *     (bounded by `CROSS_TAB_ROTATION_WAIT_MS`) for that peer's result and
 *     adopts it instead of POSTing;
 *   - the winner announces `refreshed {accessToken, user, tenant}`, which every
 *     peer adopts immediately (whether or not it was waiting);
 *   - a tab whose POST fails waits the same bound for a peer `refreshed` (it
 *     probably lost the race) or `logout` before returning `null`.
 *
 * The server never clears the cookie on a failed refresh, so a loser's 401
 * cannot wipe the winner's freshly rotated cookie. Where `BroadcastChannel` is
 * missing, `postAuthMessage` is a no-op and nothing is heard: each tab behaves
 * on its own (and a losing tab may log out — the pre-channel worst case).
 */

/**
 * How long a tab waits for a peer's refresh outcome (T-20260723-11a; Story 17.2
 * moved it onto the channel). Exported so tests advance fake timers by the real
 * bound rather than a literal.
 *
 * The cost: for a GENUINE session expiry with no winning tab every terminal
 * path — the interceptor 401, the chat 401, the authedEventSource reconnect —
 * reaches /login `CROSS_TAB_ROTATION_WAIT_MS` later than it otherwise would.
 */
export const CROSS_TAB_ROTATION_WAIT_MS = 2000;

/**
 * A peer's `refreshing` with no result after this long is treated as abandoned
 * (the tab closed mid-POST): this tab stops deferring to it and POSTs itself.
 */
const PEER_REFRESH_STALE_MS = 10_000;

/** When a peer last announced `refreshing` with no outcome yet (ms epoch), or null. */
let peerRefreshingSince: number | null = null;

/**
 * Bumped on every peer outcome. A failed POST compares it to the value at the
 * POST's start: a peer `refreshed` that landed DURING the POST (the winner's
 * broadcast often beats the loser's 401) is already in memory and is used at
 * once, with no wait.
 */
let peerOutcomeSeq = 0;
let lastPeerOutcome: 'refreshed' | 'logout' | null = null;

type PeerOutcome = { kind: 'refreshed'; accessToken: string } | { kind: 'logout' };
const peerOutcomeWaiters = new Set<(outcome: PeerOutcome) => void>();

/** Write the identity JSON a refresh / peer broadcast carried. Never throws. */
const persistIdentity = (user: unknown, tenant: unknown): void => {
  try {
    if (user && typeof user === 'object') localStorage.setItem('user', JSON.stringify(user));
    if (tenant && typeof tenant === 'object') localStorage.setItem('tenant', JSON.stringify(tenant));
  } catch {
    // Storage unavailable — identity is also in AuthContext state.
  }
};

const handlePeerMessage = (message: AuthMessage): void => {
  if (message.type === 'refreshing') {
    peerRefreshingSince = Date.now();
    return;
  }
  peerRefreshingSince = null;
  peerOutcomeSeq += 1;
  let outcome: PeerOutcome;
  if (message.type === 'refreshed') {
    // Adopt the winner's token, whoever was waiting for it.
    setAccessToken(message.accessToken);
    lastPeerOutcome = 'refreshed';
    outcome = { kind: 'refreshed', accessToken: message.accessToken };
  } else {
    clearAccessToken();
    lastPeerOutcome = 'logout';
    outcome = { kind: 'logout' };
  }
  for (const waiter of [...peerOutcomeWaiters]) waiter(outcome);
};

let peerListenerInstalled = false;
/** Subscribe this module to peer messages once, on first use. */
const ensurePeerListener = (): void => {
  if (peerListenerInstalled) return;
  peerListenerInstalled = true;
  onAuthMessage(handlePeerMessage);
};

/** Install the listener now so a tab adopts peer tokens before its first request. */
ensurePeerListener();

const peerRefreshInFlight = (): boolean =>
  peerRefreshingSince !== null && Date.now() - peerRefreshingSince < PEER_REFRESH_STALE_MS;

/** Wait up to `CROSS_TAB_ROTATION_WAIT_MS` for a peer `refreshed` / `logout`. */
const awaitPeerOutcome = (): Promise<PeerOutcome | null> =>
  new Promise((resolve) => {
    const settle = (value: PeerOutcome | null) => {
      clearTimeout(deadline);
      peerOutcomeWaiters.delete(settle);
      resolve(value);
    };
    peerOutcomeWaiters.add(settle);
    const deadline = setTimeout(() => settle(null), CROSS_TAB_ROTATION_WAIT_MS);
  });

/**
 * Test hook: forget every peer-coordination fact (the module listener stays
 * installed). Waiters in flight are released with `null` by their own timers.
 */
export const __resetCrossTabStateForTests = (): void => {
  peerRefreshingSince = null;
  peerOutcomeSeq = 0;
  lastPeerOutcome = null;
  peerOutcomeWaiters.clear();
  lastRefreshFailure = null;
  isRefreshing = false;
  failedQueue = [];
};

/**
 * The one-time legacy migration (Story 17.2): a session created before the
 * cookie existed still has its refresh token in localStorage. Read it so the
 * first refresh after deploy can send it in the body (the harness accepts a
 * body token only when no cookie is present, and answers with the cookie).
 */
const readLegacyRefreshToken = (): string | null => {
  try {
    return localStorage.getItem('refresh_token');
  } catch {
    return null;
  }
};

/**
 * Why the last `refreshAccessToken` returned `null`. `'rejected'` means the
 * session is over: the harness answered 401 `invalid_grant` (its only
 * session-rejection status), a peer tab logged out, or there was nothing to
 * refresh. `'transient'` is anything else: a network drop, a 5xx or 502 during
 * a harness restart, a 429 from the per-IP limiter, or a 403
 * `origin_not_allowed` (a proxy fault). Then the cookie may still be valid, so
 * callers must not wipe the session over it. `null` after a success.
 */
export type RefreshFailure = 'rejected' | 'transient';
let lastRefreshFailure: RefreshFailure | null = null;

/** Why the most recent refresh failed, or `null` if it succeeded. */
export const getLastRefreshFailure = (): RefreshFailure | null => lastRefreshFailure;

const isSessionRejection = (error: unknown): boolean =>
  axios.isAxiosError(error) && error.response?.status === 401;

/** The refresh POST itself. Bare axios so the 401 interceptor cannot recurse. */
const postRefresh = async (): Promise<string | null> => {
  const legacyRefreshToken = readLegacyRefreshToken();
  // Drop the legacy keys only once the server has ruled on them: a success or
  // a 401. A transient failure keeps them so the next attempt can migrate.
  let settled = false;
  try {
    const response = await axios.post(
      REFRESH_URL,
      legacyRefreshToken ? { refresh_token: legacyRefreshToken } : {},
      { withCredentials: true },
    );
    const { access_token, user, tenant } = (response.data ?? {}) as {
      access_token?: unknown;
      user?: unknown;
      tenant?: unknown;
    };
    if (typeof access_token !== 'string' || access_token.length === 0) {
      throw new Error('Refresh response carried no access_token');
    }
    setAccessToken(access_token);
    persistIdentity(user, tenant);
    postAuthMessage({
      type: 'refreshed',
      accessToken: access_token,
      user: (user && typeof user === 'object' ? user : null) as User | null,
      tenant: (tenant && typeof tenant === 'object' ? tenant : null) as Tenant | null,
    });
    settled = true;
    return access_token;
  } catch (error) {
    if (isSessionRejection(error)) settled = true;
    throw error;
  } finally {
    if (settled && legacyRefreshToken !== null) removeLegacyTokenKeys();
  }
};

// Helper function to refresh access token. Never throws.
const refreshAccessToken = async (): Promise<string | null> => {
  ensurePeerListener();

  // A signed-out tab (no token in memory, no session hint) has nothing to
  // refresh: answer at once, as the pre-cookie code did when it found no
  // refresh token. Without this a wrong password on /login would POST a
  // refresh, announce `refreshing` to every peer and sit out the 2 s wait.
  if (!getAccessToken() && !hasSessionHint()) {
    lastRefreshFailure = 'rejected';
    return null;
  }

  // A peer is mid-refresh on the shared cookie: wait for its verdict rather
  // than racing it for the single-use rotation.
  if (peerRefreshInFlight()) {
    const outcome = await awaitPeerOutcome();
    if (outcome?.kind === 'refreshed') {
      lastRefreshFailure = null;
      return outcome.accessToken;
    }
    if (outcome?.kind === 'logout') {
      lastRefreshFailure = 'rejected';
      return null;
    }
    // Timed out — the peer vanished. Forget its claim so the next refresh in
    // this tab does not pay the wait again, then refresh ourselves.
    peerRefreshingSince = null;
  }

  const seqAtStart = peerOutcomeSeq;
  postAuthMessage({ type: 'refreshing' });
  try {
    const token = await postRefresh();
    lastRefreshFailure = null;
    return token;
  } catch (error) {
    // Probably lost the race to a peer. Its `refreshed` may already have
    // landed during our POST (adopted into memory by `handlePeerMessage`) —
    // use it at once; otherwise wait a bounded window for it, or a logout.
    if (peerOutcomeSeq !== seqAtStart) {
      if (lastPeerOutcome === 'refreshed') {
        const adopted = getAccessToken();
        if (adopted) {
          lastRefreshFailure = null;
          return adopted;
        }
      }
      if (lastPeerOutcome === 'logout') {
        lastRefreshFailure = 'rejected';
        return null;
      }
    }
    const outcome = await awaitPeerOutcome();
    if (outcome?.kind === 'refreshed') {
      lastRefreshFailure = null;
      return outcome.accessToken;
    }
    console.error('Token refresh failed:', error);
    lastRefreshFailure =
      outcome?.kind === 'logout' || isSessionRejection(error) ? 'rejected' : 'transient';
    return null;
  }
};

/**
 * Terminal auth failure — clear every auth artefact and bounce to /login.
 *
 * T-20260723-18: this was duplicated verbatim in the 401 interceptor's
 * "refresh returned null" arm and its catch arm. It is now a single helper so
 * the axios interceptor AND the chat transport wrapper (`chatFetch.ts`) can
 * only ever fail in one way. Exported for that consumer — no other caller
 * should be inventing its own logout path.
 */
export const clearAuthAndRedirectToLogin = (): void => {
  // Delegates the storage half to the pre-existing canonical helper rather
  // than re-listing the four keys — review caught that this file was about to
  // become a THIRD byte-identical copy of `clearAuthState`.
  clearAuthState();
  // Story 30.2 (O15): carry `?next=` so signing back in returns to the exact
  // path and query the session expired on. Explicit and peer logout stay bare.
  window.location.href = loginPathFor(window.location);
};

/**
 * Refresh the access token, JOINING the interceptor's coalescing state.
 *
 * T-20260723-18: the AI SDK chat transport does not go through axios, so it
 * needs its own 401 hook — but it must NOT get its own refresh. If a chat 401
 * and an axios 401 landed together and each POSTed to the refresh endpoint,
 * the loser's rotated refresh token would already be invalidated by the winner
 * and the user would be logged out mid-turn. So this shares the exact
 * module-level `isRefreshing` / `failedQueue` above: when a refresh is already
 * in flight we queue and await its outcome, otherwise we own the refresh and
 * `processQueue` the result for everyone waiting (axios-side included).
 *
 * SCOPE OF THAT GUARANTEE — read before relying on it. Within a tab this
 * coalesces EVERY caller: the axios interceptor, the chat transport, and all
 * three AuthContext paths — the 5-minute expiry sweep, its exposed
 * `refreshAccessToken` and the mount-time rehydrate (which, since Story 17.2,
 * is how a reloaded page or a new tab gets its first token: the cookie
 * authenticates the call). No caller refreshes outside this lock — Story 17.2
 * deleted `auth.ts`'s unused `refreshToken` export. ACROSS tabs the
 * `amatis-auth` BroadcastChannel coordinates (see the cross-tab block above):
 * a tab defers to a peer's in-flight refresh, adopts any peer's `refreshed`
 * token, and a losing tab waits up to `CROSS_TAB_ROTATION_WAIT_MS` for the
 * winner's broadcast. Do not add another refresh path here.
 *
 * Never throws: returns the new access token, or `null` when the refresh
 * failed (no cookie, network error, rejected by the server). Callers
 * decide what a `null` means for them — this function deliberately does NOT
 * clear auth or redirect, so a caller can finish handling its own request
 * first (see `clearAuthAndRedirectToLogin`).
 */
export const refreshOnce = async (): Promise<string | null> => {
  if (isRefreshing) {
    // Someone else owns the in-flight refresh — wait for its verdict.
    return new Promise<string>((resolve, reject) => {
      failedQueue.push({ resolve, reject });
    }).catch(() => null);
  }

  isRefreshing = true;
  try {
    const newToken = await refreshAccessToken();
    if (newToken) {
      processQueue(null, newToken);
      return newToken;
    }
    processQueue(new Error('Token refresh failed'), null);
    return null;
  } catch (refreshError) {
    // `refreshAccessToken` swallows its own errors, so this is belt-and-braces
    // for anything unexpected. The queue must still be drained or its waiters hang forever —
    // hence the coercion: `throw undefined` would otherwise reach processQueue
    // as a falsy "error" and settle nobody.
    processQueue((refreshError as Error) ?? new Error('Token refresh failed'), null);
    return null;
  } finally {
    isRefreshing = false;
  }
};

/**
 * Wire the shared interceptors onto an axios instance. Successful responses
 * resolve to the full Axios response — callers read `response.data` (and,
 * for dbClient, headers such as `Content-Range`) themselves.
 */
function applySharedInterceptors(instance: AxiosInstance): void {
  instance.interceptors.request.use(attachAuth, (error) => {
    return Promise.reject(error);
  });

  // Response interceptor - handle errors and token refresh
  instance.interceptors.response.use(
    (response) => response,
    async (error: AxiosError) => {
      const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

      // Log errors in development. A cancelled request is not an error: TanStack
      // Query aborts in-flight fetches on unmount (StrictMode double-mounts every
      // page in dev), and those arrived here as "[API Error] undefined undefined".
      if (import.meta.env.DEV && !axios.isCancel(error)) {
        console.error('[API Error]', error.response?.status, error.response?.data);
      }

      // Handle 401 - Unauthorized (attempt token refresh)
      if (error.response?.status === 401 && originalRequest && !originalRequest._retry) {
        if (isRefreshing) {
          // If already refreshing, queue this request.
          // T-20260723-18 — mark `_retry` HERE too. This arm previously
          // replayed without it, so a replay that 401ed again re-entered this
          // branch, found `isRefreshing` false, and owned another refresh —
          // an unbounded loop. The owning arm below has always set it; this
          // one was the gap, and the chat path now feeds the same queue.
          originalRequest._retry = true;
          return new Promise((resolve, reject) => {
            failedQueue.push({ resolve, reject });
          })
            .then((token) => {
              originalRequest.headers.Authorization = `Bearer ${token}`;
              return instance(originalRequest);
            })
            .catch((err) => {
              return Promise.reject(err);
            });
        }

        originalRequest._retry = true;
        isRefreshing = true;

        try {
          const newToken = await refreshAccessToken();

          if (newToken) {
            processQueue(null, newToken);
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
            return instance(originalRequest);
          } else {
            // Refresh failed, clear auth and redirect to login
            processQueue(new Error('Token refresh failed'), null);
            clearAuthAndRedirectToLogin();
            return Promise.reject(error);
          }
        } catch (refreshError) {
          processQueue(refreshError as Error, null);
          // Refresh failed, clear auth and redirect to login
          clearAuthAndRedirectToLogin();
          return Promise.reject(refreshError);
        } finally {
          isRefreshing = false;
        }
      }

      // Handle 403 - Forbidden (Permission Denied) - AC-11.3.5
      //
      // `registration_closed` (T-20260801-1) is excluded: it is the ONE 403 on a
      // public, unauthenticated route. It does not mean "your role lacks a
      // permission", and the caller renders its own beta-signup notice — a
      // "You don't have permission to…" toast on top of that would tell an
      // anonymous visitor their account is at fault when they have no account.
      if (
        error.response?.status === 403 &&
        (error.response.data as { error?: string } | undefined)?.error !== 'registration_closed'
      ) {
        const errorData = error.response.data as { error?: string; message?: string; action?: string } | undefined;

        // Extract action context from URL or error message
        const urlPath = originalRequest?.url || '';
        const actionContext = extractActionFromPath(urlPath);

        // Create user-friendly permission error
        const permError = formatPermissionError(
          actionContext || errorData?.action || 'perform this action',
          undefined // Role is determined by backend
        );

        // Log for debugging
        logError(error, { path: urlPath, action: 'permission_denied' });

        // Show toast notification (AC-11.3.5)
        toast.error(permError.message, {
          id: 'permission-denied',
          description: permError.action,
          duration: 5000,
        });
      }

      // Handle 500+ - Server errors (AC-11.3.1)
      if (error.response?.status && error.response.status >= 500) {
        const appError = getActionableMessage(error);

        // Log for debugging (AC-11.3.3)
        logError(error, {
          path: originalRequest?.url,
          action: 'server_error',
        });

        // Show toast notification — unless the caller renders the failure
        // itself (`skipErrorToast`, story 8.3).
        if (!originalRequest?.skipErrorToast) {
          toast.error(appError.message, {
            id: 'server-error',
            description: appError.action,
            duration: 5000,
          });
        }
      }

      // Handle network errors (AC-11.3.4) — `skipErrorToast` callers render
      // their own failure here too (story 8.3).
      if (!error.response && error.code === 'ERR_NETWORK') {
        const appError = getActionableMessage(error);

        logError(error, { action: 'network_error' });

        if (!originalRequest?.skipErrorToast) {
          toast.error(appError.message, {
            id: 'network-error',
            description: appError.action,
            duration: 5000,
          });
        }
      }

      return Promise.reject(error);
    }
  );
}

applySharedInterceptors(dbClient);
applySharedInterceptors(harnessClient);

/**
 * Extract action context from URL path for permission error messages.
 * Exported for `__tests__/client.extractAction.test.ts`.
 */
export function extractActionFromPath(path: string): string | undefined {
  // T-20260916-8 — BEFORE `/settings`, because the API-keys page lives at
  // `/settings/api-keys` while its REQUESTS go to `/api/api-keys`; the ordering
  // matters only if a future caller passes a page path. Without this entry the
  // 403 toast beside the page's own message read "You don't have permission to
  // perform this action", which tells an owner-gated credential surface's user
  // nothing at all.
  if (path.includes('/api-keys')) return 'manage API keys';
  if (path.includes('/invitations')) return 'manage team invitations';
  if (path.includes('/users')) return 'manage team members';
  if (path.includes('/documents')) return 'manage documents';
  if (path.includes('/campaigns')) return 'manage campaigns';
  if (path.includes('/calendar')) return 'manage calendar';
  // Story 24.8 — page paths: `/settings` is the Settings hub and the team
  // page moved to `/settings/team`; both read 'access settings' here. The
  // team page's REQUESTS (`/api/users…`, `/api/invitations…`) keep their own
  // copy above.
  if (path.includes('/settings')) return 'access settings';
  return undefined;
}
